import React, { useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import Street from "../../assets/street.svg";
import CloudTop from "../../assets/cloudtop.svg";
import CloudMid from "../../assets/cloudmid.svg";
import CloudBottom from "../../assets/cloudbot.svg";
import LabBack from "../../assets/backgrounds/labBack.svg";
import Bridge from "../../assets/Bridge.svg";
import MinionRun from "../../assets/minion-run.svg";
import Lighting from "../../assets/Lighting Fixure.svg";
import "./StoryBackground.scss";

gsap.registerPlugin(ScrollTrigger);
ScrollTrigger.config({ ignoreMobileResize: true });

const sectionScroll = (el: HTMLElement) =>
  el.getBoundingClientRect().top + window.scrollY;

const SKY_STARS = [
  { left: "62%", top: "6%", size: 2, delay: 0.1, duration: 2.4 },
  { left: "71%", top: "11%", size: 3, delay: 0.8, duration: 3.1 },
  { left: "79%", top: "5%", size: 2, delay: 1.4, duration: 2.2 },
  { left: "88%", top: "14%", size: 4, delay: 0.3, duration: 3.6 },
  { left: "93%", top: "8%", size: 2, delay: 2.1, duration: 2.8 },
  { left: "68%", top: "22%", size: 2, delay: 1.7, duration: 2.5 },
  { left: "84%", top: "24%", size: 3, delay: 0.6, duration: 3.3 },
  { left: "91%", top: "31%", size: 2, delay: 2.4, duration: 2.1 },
  { left: "74%", top: "38%", size: 2, delay: 1.1, duration: 2.9 },
  { left: "96%", top: "42%", size: 3, delay: 0.4, duration: 3.4 },
  { left: "81%", top: "48%", size: 2, delay: 1.9, duration: 2.6 },
  { left: "89%", top: "56%", size: 2, delay: 0.9, duration: 3.0 },
  { left: "65%", top: "52%", size: 3, delay: 2.2, duration: 2.7 },
  { left: "76%", top: "63%", size: 2, delay: 1.5, duration: 3.2 },
  { left: "92%", top: "68%", size: 4, delay: 0.2, duration: 2.3 },
  { left: "70%", top: "74%", size: 2, delay: 2.6, duration: 2.8 },
  { left: "85%", top: "79%", size: 3, delay: 1.3, duration: 3.5 },
  { left: "58%", top: "9%", size: 2, delay: 1.8, duration: 2.4 },
  { left: "54%", top: "18%", size: 2, delay: 0.5, duration: 3.1 },
  { left: "48%", top: "4%", size: 3, delay: 2.0, duration: 2.6 },
  { left: "41%", top: "8%", size: 2, delay: 0.7, duration: 2.9 },
  { left: "12%", top: "6%", size: 2, delay: 1.6, duration: 3.3 },
  { left: "6%", top: "16%", size: 3, delay: 2.3, duration: 2.2 },
  { left: "18%", top: "3%", size: 2, delay: 0.35, duration: 2.7 },
  { left: "3%", top: "38%", size: 2, delay: 1.2, duration: 3.0 },
  { left: "8%", top: "58%", size: 3, delay: 2.5, duration: 2.5 },
  { left: "15%", top: "78%", size: 2, delay: 0.85, duration: 3.4 },
  { left: "51%", top: "82%", size: 2, delay: 1.45, duration: 2.1 },
  { left: "61%", top: "88%", size: 3, delay: 0.15, duration: 2.8 },
  { left: "97%", top: "88%", size: 2, delay: 2.05, duration: 3.2 },
  { left: "44%", top: "71%", size: 2, delay: 1.75, duration: 2.4 },
  { left: "33%", top: "88%", size: 3, delay: 0.55, duration: 3.1 },
];

export function StoryBackground() {
  const backgroundRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const root = backgroundRef.current;
      const assignments = document.getElementById("assignments");
      const lectures = document.getElementById("lectures");
      const sections = document.getElementById("sections");
      const codealong = document.getElementById("codealong");
      const hours = document.getElementById("hours");
      const resources = document.getElementById("resources");
      const staff = document.getElementById("staff");

      if (
        !root ||
        !assignments ||
        !lectures ||
        !sections ||
        !codealong ||
        !hours ||
        !resources ||
        !staff
      ) {
        return;
      }

      const sky = root.querySelector<HTMLElement>(".story-sky");
      const street = root.querySelector<HTMLElement>(".story-street");
      const labLayer = ".story-lab";
      const bridgeLayer = ".story-bridge";
      const minionLayer = ".story-bridge-minion";
      const lightLayer = ".story-light";
      const vignetteLayer = ".story-vignette";
      const dawnClouds = [
        ".story-cloud--back",
        ".story-cloud--middle",
      ];
      const noonClouds = [
        ".story-cloud--high",
        ".story-cloud--far",
      ];
      const duskClouds = [
        ".story-cloud--late",
        ".story-cloud--late-far",
      ];

      gsap.set(
        [
          ".story-street",
          vignetteLayer,
          ".story-cloud",
          labLayer,
          bridgeLayer,
          lightLayer,
        ],
        { autoAlpha: 0 }
      );
      gsap.set(".story-cloud--far", { scaleX: -1 });
      gsap.set(".story-cloud--late-far", { scaleX: -1 });
      gsap.set(
        [".story-cloud--front", ".story-cloud--low"],
        { display: "none" }
      );

      const staffHeader =
        document.querySelector<HTMLElement>("#staff .staff-logo") ?? staff;

      const navOffset = () =>
        document.querySelector<HTMLElement>(".nav-bar")?.offsetHeight ?? 68;
      const restOffset = () => window.innerHeight * 0.08;

      const enterDistance = () => {
        const startAt = sectionScroll(sections) - window.innerHeight * 0.9;
        const endAt = sectionScroll(resources);
        return Math.max(1, endAt - startAt);
      };

      // Hold until the STAFF heading meets the city bottom, which rests
      // a little below the viewport edge.
      const holdDistance = () => {
        const startAt = sectionScroll(resources);
        const endAt =
          sectionScroll(staffHeader) - window.innerHeight + restOffset();
        return Math.max(1, endAt - startAt);
      };

      // 1:1 with the heading from that meeting point up to the navbar.
      const cityExitDistance = () =>
        Math.max(1, window.innerHeight - restOffset() - navOffset());

      // Lab wash + lamp share this window so they come on together
      // as the skyline starts to leave, not stretched across the whole exit.
      const labRevealDistance = () => window.innerHeight * 0.8;

      const skyDark = { top: "#001F33", mid: "#016AB6", bot: "#BB9FD1" };
      const skyPurple = { top: "#BB9FD1", mid: "#EFCBCB", bot: "#c4a3c7" };
      const skyDay = { top: "#4a9fd8", mid: "#8ecdf0", bot: "#e4f6ff" };
      const skyLab = { top: "#0d8c8e", mid: "#4fa2a7", bot: "#78afb4" };

      const mixSky = (
        from: typeof skyDark,
        to: typeof skyDark,
        progress: number
      ) => ({
        top: gsap.utils.interpolate(from.top, to.top, progress),
        mid: gsap.utils.interpolate(from.mid, to.mid, progress),
        bot: gsap.utils.interpolate(from.bot, to.bot, progress),
      });

      const skyColorsForScroll = (scroll: number) => {
        const assignAt = Math.max(1, sectionScroll(assignments));
        const codeAt = Math.max(assignAt + 1, sectionScroll(codealong));
        const cityStart = Math.max(
          codeAt + 1,
          sectionScroll(staffHeader) - window.innerHeight + restOffset()
        );
        const revealEnd = cityStart + labRevealDistance();

        if (scroll <= 0) {
          return skyDark;
        }
        if (scroll < assignAt) {
          return mixSky(skyDark, skyPurple, scroll / assignAt);
        }
        if (scroll < codeAt) {
          return mixSky(
            skyPurple,
            skyDay,
            gsap.utils.mapRange(assignAt, codeAt, 0, 1, scroll)
          );
        }
        if (scroll < cityStart) {
          return skyDay;
        }
        if (scroll < revealEnd) {
          return mixSky(
            skyDay,
            skyLab,
            gsap.utils.mapRange(cityStart, revealEnd, 0, 1, scroll)
          );
        }
        return skyLab;
      };

      if (sky) {
        const applySky = (scroll = window.scrollY) => {
          const colors = skyColorsForScroll(scroll);
          sky.style.setProperty("--sky-top", colors.top);
          sky.style.setProperty("--sky-mid", colors.mid);
          sky.style.setProperty("--sky-bot", colors.bot);
        };

        applySky();

        ScrollTrigger.create({
          start: 0,
          end: "max",
          onUpdate: (self) => applySky(self.scroll()),
          onRefresh: (self) => applySky(self.scroll()),
        });
      }

      gsap.fromTo(
        ".story-stars",
        { autoAlpha: 1 },
        {
          autoAlpha: 0,
          ease: "none",
          scrollTrigger: {
            trigger: assignments,
            start: "top 88%",
            end: "top 42%",
            scrub: true,
          },
        }
      );

      gsap.timeline({
        scrollTrigger: {
          trigger: assignments,
          endTrigger: lectures,
          start: "top bottom",
          end: "bottom top",
          scrub: 0.7,
        },
      })
        .to(dawnClouds[0], { autoAlpha: 0.58, duration: 0.18, ease: "none" }, 0)
        .to(dawnClouds[1], { autoAlpha: 0.7, duration: 0.18, ease: "none" }, 0.04)
        .fromTo(dawnClouds[0], { xPercent: -100 }, { xPercent: 0, duration: 1, ease: "none" }, 0)
        .fromTo(dawnClouds[1], { xPercent: 100 }, { xPercent: 0, duration: 1, ease: "none" }, 0)
        .to(dawnClouds, { autoAlpha: 0, duration: 0.16, ease: "none" }, 0.84);

      gsap.timeline({
        scrollTrigger: {
          trigger: lectures,
          endTrigger: hours,
          start: "top 70%",
          end: "top 15%",
          scrub: 0.85,
        },
      })
        .to(noonClouds[0], { autoAlpha: 0.5, duration: 0.16, ease: "none" }, 0)
        .to(noonClouds[1], { autoAlpha: 0.62, duration: 0.16, ease: "none" }, 0.05)
        .fromTo(noonClouds[0], { xPercent: -100 }, { xPercent: 0, duration: 1, ease: "none" }, 0)
        .fromTo(noonClouds[1], { xPercent: 100 }, { xPercent: 0, duration: 1, ease: "none" }, 0)
        .to(noonClouds, { autoAlpha: 0, duration: 0.18, ease: "none" }, 0.8);

      gsap.timeline({
        scrollTrigger: {
          trigger: hours,
          endTrigger: resources,
          start: "top 80%",
          end: "top 10%",
          scrub: 0.9,
        },
      })
        .to(duskClouds[0], { autoAlpha: 0.52, duration: 0.18, ease: "none" }, 0)
        .to(duskClouds[1], { autoAlpha: 0.7, duration: 0.18, ease: "none" }, 0.06)
        .fromTo(duskClouds[0], { xPercent: -100 }, { xPercent: 0, duration: 1, ease: "none" }, 0)
        .fromTo(duskClouds[1], { xPercent: 100 }, { xPercent: 0, duration: 1, ease: "none" }, 0)
        .to(duskClouds, { autoAlpha: 0, duration: 0.2, ease: "none" }, 0.78);

      if (street) {
        // City is `position: absolute; bottom: 0`. GSAP `y` is extra offset:
        //   y = restOffset   → skyline sits a little lower in the viewport
        //   y = -height → the full SVG has moved above the viewport
        const holdY = () => window.innerHeight * 0.14;
        const enterY = () => window.innerHeight;
        const exitY = () => -(street.offsetHeight || window.innerHeight);

        gsap.set(street, { y: enterY() });

        gsap
          .timeline({
            defaults: { ease: "none", force3D: true },
            scrollTrigger: {
              trigger: sections,
              start: "top 110%",
              end: () =>
                `+=${enterDistance() + holdDistance() + cityExitDistance()}`,
              scrub: true,
              invalidateOnRefresh: true,
              id: "city-scroll",
            },
          })
          .fromTo(
            street,
            { y: enterY, autoAlpha: 0 },
            { y: holdY, autoAlpha: 0.8, duration: enterDistance }
          )
          .to(street, { y: holdY, duration: holdDistance })
          .to(street, {
            y: exitY,
            autoAlpha: 0,
            duration: cityExitDistance,
          });
      }

      gsap
        .timeline({
          scrollTrigger: {
            trigger: staffHeader,
            start: "top bottom",
            end: () => `+=${labRevealDistance()}`,
            scrub: true,
            invalidateOnRefresh: true,
          },
        })
        .to(labLayer, { autoAlpha: 0.92, ease: "none" }, 0)
        .to(lightLayer, { autoAlpha: 1, ease: "none" }, 0)
        .to(vignetteLayer, { autoAlpha: 0.48, ease: "none" }, 0);

      const bridgeStartY = () => window.innerHeight * 0.35;
      const bridgeEndY = () => -window.innerHeight * 0.55;
      const minion = root.querySelector<HTMLElement>(minionLayer);
      const minionEnterX = () => window.innerWidth;
      const minionExitX = () => -((minion?.offsetWidth ?? 160) + 32);

      gsap.set(bridgeLayer, { y: bridgeStartY() });
      gsap.set(minionLayer, { x: minionEnterX() });

      gsap.fromTo(
        bridgeLayer,
        { autoAlpha: 0 },
        {
          autoAlpha: 1,
          ease: "none",
          scrollTrigger: {
            trigger: staffHeader,
            start: "top bottom",
            end: () => `+=${labRevealDistance()}`,
            scrub: true,
            invalidateOnRefresh: true,
          },
        }
      );

      gsap.fromTo(
        bridgeLayer,
        { y: bridgeStartY },
        {
          y: bridgeEndY,
          ease: "none",
          force3D: true,
          scrollTrigger: {
            trigger: staffHeader,
            endTrigger: staff,
            start: "top bottom",
            end: "bottom bottom",
            scrub: 1.5,
            invalidateOnRefresh: true,
          },
        }
      );

      gsap.fromTo(
        minionLayer,
        { x: minionEnterX },
        {
          x: minionExitX,
          ease: "none",
          force3D: true,
          scrollTrigger: {
            trigger: staffHeader,
            endTrigger: staff,
            start: () =>
              sectionScroll(staffHeader) - window.innerHeight + labRevealDistance(),
            end: "bottom bottom",
            scrub: 0.85,
            invalidateOnRefresh: true,
          },
        }
      );

      gsap.to(labLayer, {
        yPercent: -10,
        xPercent: -6,
        ease: "none",
        force3D: true,
        scrollTrigger: {
          trigger: staffHeader,
          endTrigger: staff,
          start: "top bottom",
          end: "bottom bottom",
          scrub: 1,
          invalidateOnRefresh: true,
        },
      });

      const images = root.querySelectorAll("img");
      images.forEach((image) => {
        if (!image.complete) {
          image.addEventListener("load", () => ScrollTrigger.refresh(), { once: true });
        }
      });
    },
    { scope: backgroundRef }
  );

  return (
    <div className="story-background" ref={backgroundRef} aria-hidden="true">
      <div className="story-sky" />
      <div className="story-stars" aria-hidden="true">
        {SKY_STARS.map((star, index) => (
          <span
            key={index}
            className="story-star"
            style={{
              left: star.left,
              top: star.top,
              width: star.size,
              height: star.size,
              animationDelay: `${star.delay}s`,
              animationDuration: `${star.duration}s`,
            }}
          />
        ))}
      </div>
      <img className="story-lab" src={LabBack} alt="" />
      <div className="story-light">
        <img className="story-light-inner" src={Lighting} alt="" />
      </div>
      <img className="story-cloud story-cloud--back" src={CloudTop} alt="" />
      <img className="story-cloud story-cloud--middle" src={CloudMid} alt="" />
      <img className="story-cloud story-cloud--front" src={CloudBottom} alt="" />
      <img className="story-cloud story-cloud--high" src={CloudTop} alt="" />
      <img className="story-cloud story-cloud--far" src={CloudMid} alt="" />
      <img className="story-cloud story-cloud--low" src={CloudBottom} alt="" />
      <img className="story-cloud story-cloud--late" src={CloudTop} alt="" />
      <img className="story-cloud story-cloud--late-far" src={CloudMid} alt="" />
      <div className="story-bridge">
        <img className="story-bridge-art" src={Bridge} alt="" />
        <img className="story-bridge-minion" src={MinionRun} alt="" />
      </div>
      <img className="story-street" src={Street} alt="" />
      <div className="story-vignette" />
    </div>
  );
}
